import { Lab } from '../amplify/appsync/query';
import { LabEntry, LabResult } from '../components/dashboard/llm/common/models';

/**
 * Extended Lab interface that includes optional scan_multi-specific fields
 */
export interface UnifiedLab extends Lab {
    _citation_id?: string;  // For scan_multi data
    _is_new?: boolean;      // For diff highlighting
}

/**
 * Maps scan_multi component_abnormal flags to database status values
 */
export function createAbnormalStatusFromFlag(abnormalFlag: string): string {
    if (!abnormalFlag) return "";

    const normalizedFlag = abnormalFlag.toUpperCase().trim();

    // Map specific patterns
    if (normalizedFlag.includes("CRITICAL") && normalizedFlag.includes("HIGH")) {
        return "CRITICAL_HIGH";
    } else if (normalizedFlag.includes("CRITICAL") && normalizedFlag.includes("LOW")) {
        return "CRITICAL_LOW";
    } else if (normalizedFlag.includes("CRITICAL")) {
        return "CRITICAL";
    } else if (normalizedFlag.includes("HIGH") || normalizedFlag === "H") {
        return "ABNORMAL_HIGH";
    } else if (normalizedFlag.includes("LOW") || normalizedFlag === "L") {
        return "ABNORMAL_LOW";
    } else if (normalizedFlag.includes("ABNORMAL") || normalizedFlag === "A") {
        return "ABNORMAL";
    }

    // Default to ABNORMAL if any flag is present
    return "ABNORMAL";
}

/**
 * Converts an ISO 8601 timestamp string to Unix epoch (seconds)
 */
function convertTimestampToEpoch(isoTimestamp: string): number {
    try {
        const date = new Date(isoTimestamp);
        if (isNaN(date.getTime())) {
            console.warn(`Invalid timestamp: ${isoTimestamp}, using current time`);
            return Date.now() / 1000;
        }
        return date.getTime() / 1000;
    } catch (error) {
        console.warn(`Error parsing timestamp: ${isoTimestamp}`, error);
        return Date.now() / 1000;
    }
}

/**
 * Converts a string value to a number, returning null if conversion fails
 */
function convertValueToNumber(value: string): number | null {
    if (!value || value.trim() === "") return null;

    const parsed = parseFloat(value);
    return isNaN(parsed) ? null : parsed;
}

/**
 * Converts scan_multi lab format to Lab[][] format expected by Labs.tsx
 *
 * @param scanMultiLabs - Labs object from scan_multi: { labName: { component_units, results } }
 * @param oldScanMultiLabs - Optional old labs for diff comparison
 * @returns Lab[][] - Array of lab panels grouped by lab_group
 */
export function convertScanMultiLabsToLabArray(
    scanMultiLabs: Record<string, LabEntry>,
    oldScanMultiLabs?: Record<string, LabEntry>
): Lab[][] {
    if (!scanMultiLabs || Object.keys(scanMultiLabs).length === 0) {
        return [];
    }

    // First, flatten all lab results into a single array of Lab objects
    const allLabs: UnifiedLab[] = [];

    // Create a lookup for old labs if diff comparison is needed
    const oldLabsLookup = new Map<string, LabResult[]>();
    if (oldScanMultiLabs) {
        Object.entries(oldScanMultiLabs).forEach(([labName, labEntry]) => {
            if (labEntry?.results) {
                oldLabsLookup.set(labName, labEntry.results);
            }
        });
    }

    Object.entries(scanMultiLabs).forEach(([labName, labEntry]) => {
        if (!labEntry || !labEntry.results) return;

        labEntry.results.forEach((result: LabResult) => {
            // Check if this is a new lab result
            let isNew = false;
            if (oldScanMultiLabs && oldLabsLookup.has(labName)) {
                const oldResults = oldLabsLookup.get(labName)!;
                // Check if this exact result existed in old data (match by result_time and component_value)
                const existed = oldResults.some(
                    old => old.result_time === result.result_time &&
                           old.component_value === result.component_value
                );
                isNew = !existed;
            }

            const lab: UnifiedLab = {
                lab_name: labName,
                lab_group: result.lab_group || "Ungrouped",
                acquisition_time: convertTimestampToEpoch(result.result_time),
                component_value: convertValueToNumber(result.component_value),
                component_result: result.component_value, // Keep original string value
                component_unit: labEntry.component_units || "",
                normal_range_low: null,  // Not available in scan_multi
                normal_range_high: null, // Not available in scan_multi
                status: createAbnormalStatusFromFlag(result.component_abnormal),
                _citation_id: result._citation_id,
                _is_new: isNew,
            };

            allLabs.push(lab);
        });
    });

    // Group labs by lab_group
    const labsByGroup = new Map<string, Lab[]>();

    allLabs.forEach((lab) => {
        const group = lab.lab_group;
        if (!labsByGroup.has(group)) {
            labsByGroup.set(group, []);
        }
        labsByGroup.get(group)!.push(lab);
    });

    // Convert map to array and sort groups alphabetically
    const sortedGroups = Array.from(labsByGroup.keys()).sort();
    const result: Lab[][] = sortedGroups.map((group) => labsByGroup.get(group)!);

    return result;
}

/**
 * Groups flat Lab array by lab_group (used for live database data)
 * This is similar to parseLabsDataForModal but returns UnifiedLab[][]
 */
export function groupLabsByGroup(labs: Lab[]): Lab[][] {
    if (!labs || labs.length === 0) {
        return [];
    }

    const labsByGroup = new Map<string, Lab[]>();

    labs.forEach((lab) => {
        const group = lab.lab_group || "Ungrouped";
        if (!labsByGroup.has(group)) {
            labsByGroup.set(group, []);
        }
        labsByGroup.get(group)!.push(lab);
    });

    // Convert map to array and sort groups alphabetically
    const sortedGroups = Array.from(labsByGroup.keys()).sort();
    const result: Lab[][] = sortedGroups.map((group) => labsByGroup.get(group)!);

    return result;
}
